// Shared Sentry configuration for React/Vite admin application
// https://docs.sentry.io/platforms/javascript/guides/react/

import * as Sentry from '@sentry/react'
import { env } from '@/config/env'
import { isChunkLoadError, isExpectedAuthError } from '@/utils/error'
import { isThirdPartyOnlyEvent } from './sentry-third-party'

const { sentryDsn, sentryRelease, dev, env: appEnv, mode: appMode, prod } = env

const shouldInitSentry = sentryDsn && !dev

// Function to get Sentry release version
const getSentryRelease = () => {
  // Priority order:
  // 1. Environment variable override (Vite uses VITE_ prefix)
  // 2. Import meta env (Vite's way of accessing env vars)

  if (sentryRelease) {
    return sentryRelease
  }

  // Default fallback
  return '0.0.0'
}

// Centralized Sentry configuration
export const getSentryConfig = (): Sentry.BrowserOptions => ({
  dsn: sentryDsn,
  environment: appEnv || appMode,

  // Set release version for tracking
  release: getSentryRelease(),

  // Define how likely traces are sampled. Adjust this value in production
  tracesSampleRate: prod ? 0.1 : 1.0,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: dev,

  // Configure integrations
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // Capture 10% of all sessions,
      // plus 100% of sessions with an error
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],

  // Configure replay sampling rates
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  // Sentry is only initialized outside dev (see shouldInitSentry), so beforeSend just filters out
  // events we never want captured: chunk load errors, expected passkey/WebAuthn failures, and errors
  // whose stack lies entirely in third-party (anonymous / browser-extension) code.
  beforeSend(event) {
    const values = event.exception?.values ?? []
    if (values[0] && isChunkLoadError(values[0])) {
      return null // Don't send chunk load errors to Sentry
    }
    // Checked across every linked exception, not just the first: ox wraps the browser's
    // NotAllowedError in CredentialCreationFailedError and wagmi/viem wrap that again, so the
    // exception identifying a cancelled prompt is never the top one. A wrapper and its cause are one
    // logical failure, so a cancel anywhere in the chain means the whole event is a cancel. Kept off
    // the chunk-load check, where widening would let one incidental sibling of an AggregateError
    // discard a genuine bug alongside it.
    if (values.some((value) => isExpectedAuthError(value))) {
      return null // Expected passkey/WebAuthn failures are shown to the user, not bugs
    }
    // Errors thrown by browser-extension scripts injected into the page (e.g. a wallet's string-eval
    // refused by our CSP) carry only <anonymous> / *-extension:// frames. They are not admin bugs.
    // The CSP refusal itself is intended; never relax script-src to silence these.
    if (isThirdPartyOnlyEvent(event)) {
      return null
    }

    return event
  },
})

export const initSentry = () => {
  // Only initialize if DSN is provided and not in development
  if (shouldInitSentry) {
    Sentry.init(getSentryConfig())
  }
}
